import { ref, watch } from 'vue'
import type { AuthUser, Student } from '@/types/user';

const isLoading = ref(false)
const error = ref<string | null>(null)

type State = {
  user: Maybe<AuthUser>,
  student: Maybe<Student>
}

export const useAuth = () => {
  const hasSession = useCookie("XSRF-TOKEN", {
    maxAge: undefined,
    expires: undefined
  });

  const state = useState<State>('auth.state', () => ({
    user: null,
    student: null
  }))

  const authUser = computed<AuthUser | null>({
    get: (): AuthUser | null => {
      return state.value.user ?? null
    },
    set: (value: AuthUser | null) => {
      state.value.user = value ?? null
    }
  })

  const isAuthenticated = computed(() => {
    return !!(authUser.value && hasSession.value)
  })

  function validateSession() {
    if (hasSession.value) return
    
    return $api("/sanctum/csrf-cookie");
  };

  async function setUser(user: AuthUser | null, student: Student | null = null) {
    const authId = useCookie("auth-id", {
      maxAge: undefined,
      expires: undefined
    });

    state.value.user = user;
    state.value.student = student;
    authId.value = user?.id?.toString()
  };

  // Fetch current user data from API
  const fetchCurrentUser = async () => {
    const { $api } = useNuxtApp()
    const response = await $api('/api/me') as any
    
    if (!response || !response.data) {
      throw new Error('Failed to fetch current user: Invalid response')
    }
    
    await setUser(response.data, null)
    return response.data
  };

  const login = async (email: string, password: string, rememberMe: boolean, redirectTo: string = '/dashboard') => {
    isLoading.value = true
    error.value = null
  
    try {
      await validateSession()

      const response = await $postApi<any>("/api/login", {
        body: { email, password },
      }).catch((err: any) => {
        throw err
      });

      console.log('Login API response:', response)
      
      // Handle different response structures
      let user: AuthUser & { student?: Student }
      
      if (response.data && response.data.user) {
        user = response.data.user
      } else if (response.user) {
        user = response.user
      } else {
        throw new Error('Invalid response structure from login API')
      }

      // Extract student data from user object if it exists
      const studentData = user?.student
      await setUser(user ?? null, studentData ?? null);

      // Refresh user data from API to ensure we have the latest info
      await fetchCurrentUser()

      console.log('Login successful, redirecting to:', redirectTo)
      console.log('User data set:', authUser.value)
      
      try {
        await navigateTo(redirectTo)
        console.log('Navigation completed')
      } catch (navError) {
        console.error('Navigation failed:', navError)
        // Fallback to window.location if navigateTo fails
        if (process.client) {
          window.location.href = redirectTo
        }
      }
  
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'An error occurred during login'
      throw error.value
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    isLoading.value = true
    try {
      try {
        await $postApi("/api/logout")
      } catch (logoutError) {
        console.error('Backend logout failed:', logoutError)
      }

      reset()
      
      await navigateTo('/login', { replace: true })

    } catch (err) {
      error.value = err instanceof Error ? err.message : 'An error occurred during logout'
      reset()
      await navigateTo('/login', { replace: true })
    } finally {
      isLoading.value = false
    }
  }

  function reset() {
    const showMessage = useCookie<boolean>('show-welcome-message', {
      maxAge: undefined,
      expires: undefined
    });
    const authId = useCookie("auth-id", {
      maxAge: undefined,
      expires: undefined
    });

    clearNuxtData()
    setUser(null)
    hasSession.value = null
    authId.value = null
    showMessage.value = false
  }

  return {
    ...toRefs(state.value), // Update to use .value since we're using useState
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    authUser,
    setUser,
    fetchCurrentUser,
    reset,
  }
} 